import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Save, RotateCcw } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

import {
  useSimulationSettings,
  useUpdateSimulationSettings,
  type SimulationSettingsData,
} from '@/entities/company'

type FormState = Omit<SimulationSettingsData, 'defaults'>

function formFromData(data: SimulationSettingsData): FormState {
  return {
    customer_model: data.customer_model,
    evaluator_model: data.evaluator_model,
    qa_import_model: data.qa_import_model,
    qa_import_prompts: { ...data.qa_import_prompts },
  }
}

export function SimulationSettingsPage() {
  const { data: settings, isLoading } = useSimulationSettings()
  const updateSettings = useUpdateSimulationSettings()

  const [form, setForm] = useState<FormState | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (settings && !loaded) {
      setForm(formFromData(settings))
      setLoaded(true)
    }
  }, [settings, loaded])

  const isDirty =
    loaded &&
    form &&
    settings &&
    JSON.stringify(formFromData(settings)) !== JSON.stringify(form)

  const handleSave = async () => {
    if (!form) return
    try {
      await updateSettings.mutateAsync(form)
      toast.success('Simulation settings saved')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save settings')
    }
  }

  const resetPrompt = (key: keyof FormState['qa_import_prompts']) => {
    if (!form || !settings) return
    setForm({
      ...form,
      qa_import_prompts: {
        ...form.qa_import_prompts,
        [key]: settings.defaults.qa_import_prompts[key],
      },
    })
  }

  if (isLoading || !form) {
    return (
      <div className="w-full h-full p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-48" />
          <div className="h-32 bg-muted rounded" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full p-4 overflow-y-auto">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-heading font-bold mb-1">Simulation Settings</h1>
          <p className="text-detail text-muted-foreground">
            Configure models and prompts used for test simulations and QA imports
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={!isDirty || updateSettings.isPending}
        >
          <Save className="w-3 h-3" />
          {updateSettings.isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <div className="space-y-4 max-w-3xl">
        <Card>
          <CardHeader className="border-b py-2">
            <CardTitle className="text-body">Test Models</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 pb-4 space-y-4">
            <div className="space-y-1">
              <label className="text-detail font-medium text-muted-foreground">
                Customer Model
              </label>
              <Input
                value={form.customer_model}
                onChange={(e) => setForm({ ...form, customer_model: e.target.value })}
                placeholder={settings?.defaults.customer_model}
                className="h-8 max-w-sm"
              />
              <p className="text-[11px] text-muted-foreground leading-tight">
                Model used to simulate the customer during test conversations
              </p>
            </div>
            <div className="space-y-1">
              <label className="text-detail font-medium text-muted-foreground">
                Evaluator Model
              </label>
              <Input
                value={form.evaluator_model}
                onChange={(e) => setForm({ ...form, evaluator_model: e.target.value })}
                placeholder={settings?.defaults.evaluator_model}
                className="h-8 max-w-sm"
              />
              <p className="text-[11px] text-muted-foreground leading-tight">
                Model used to evaluate test conversation results
              </p>
            </div>
            <div className="space-y-1">
              <label className="text-detail font-medium text-muted-foreground">
                QA Import Model
              </label>
              <Input
                value={form.qa_import_model}
                onChange={(e) => setForm({ ...form, qa_import_model: e.target.value })}
                placeholder={settings?.defaults.qa_import_model}
                className="h-8 max-w-sm"
              />
              <p className="text-[11px] text-muted-foreground leading-tight">
                Model used to generate evaluation prompts when importing from QA
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b py-2">
            <CardTitle className="text-body">QA Import Prompts</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 pb-4 space-y-4">
            <PromptField
              label="Generate Evaluation Prompt"
              description="System prompt for generating evaluation checklists from QA feedback"
              value={form.qa_import_prompts.generate_evaluation}
              onChange={(v) =>
                setForm({
                  ...form,
                  qa_import_prompts: { ...form.qa_import_prompts, generate_evaluation: v },
                })
              }
              onReset={() => resetPrompt('generate_evaluation')}
            />
            <PromptField
              label="Test Agent Prompt"
              description="Customer simulation prompt used when importing a test from QA"
              value={form.qa_import_prompts.test_agent}
              onChange={(v) =>
                setForm({
                  ...form,
                  qa_import_prompts: { ...form.qa_import_prompts, test_agent: v },
                })
              }
              onReset={() => resetPrompt('test_agent')}
            />
            <PromptField
              label="Simple Evaluation Prompt"
              description="Fallback evaluation prompt when checklist generation fails"
              value={form.qa_import_prompts.evaluation_simple}
              onChange={(v) =>
                setForm({
                  ...form,
                  qa_import_prompts: { ...form.qa_import_prompts, evaluation_simple: v },
                })
              }
              onReset={() => resetPrompt('evaluation_simple')}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function PromptField({
  label,
  description,
  value,
  onChange,
  onReset,
}: {
  label: string
  description: string
  value: string
  onChange: (v: string) => void
  onReset: () => void
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-detail font-medium text-muted-foreground">{label}</label>
        <Button variant="ghost" size="sm" className="h-6 text-detail" onClick={onReset}>
          <RotateCcw className="w-3 h-3 mr-1" />
          Reset to Default
        </Button>
      </div>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        className="text-detail font-mono"
      />
      <p className="text-[11px] text-muted-foreground leading-tight">{description}</p>
    </div>
  )
}
